// biome-ignore-all lint/suspicious/useIterableCallbackReturn: TODO - address later
// biome-ignore-all lint/correctness/useHookAtTopLevel: TODO - address later
// biome-ignore-all lint/performance/noAccumulatingSpread: TODO - address later
import { useMutation, useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type { FilterGroup } from "@/lib/filters";
import {
  processQueryParams,
  type QueryParams,
} from "@/lib/process-query-params";

/**
 * Field definition for custom channel fields
 */
export interface CustomChannelFieldDefinition {
  id: string;
  display_name: string;
  data_type: string;
  enum_values?: string[];
}

/**
 * Custom channel metrics (matching backend stub)
 * Note: sourced_opportunities metrics are only relevant to campaigns and channels
 * (they require SFDC campaign source IDs), so they are excluded from custom channels
 */
export interface CustomChannelMetrics {
  accounts_engaged?: number;
  total_engagements?: number;
  people_engaged?: number;
  opportunities_influenced?: number;
  engagements_per_account?: number;
  opportunities_influenced_amount?: number;
  opportunities_influenced_won?: number;
  opportunities_influenced_won_amount?: number;
  opportunities_influenced_in_cycle?: number;
  opportunities_influenced_in_cycle_amount?: number;
  opportunities_created_post_engagement?: number;
  opportunities_created_post_engagement_amount?: number;
  progressed_opportunities?: number;
  progressed_opportunities_amount?: number;
  deal_contacts_engaged?: number;
  engagements_per_deal_contact?: number;
  accounts_reactivated?: number;
  accounts_activated?: number;
  accounts_without_opps?: number;
  days_since_first_engagement?: number;
  days_since_latest_engagement?: number;
  engagements_per_person?: number;
  first_engagement_date?: string;
  last_engagement_date?: string;
  opportunities_activated?: number;
  opportunities_activated_amount?: number;
  opportunities_activated_won?: number;
  opportunities_activated_won_amount?: number;
}

/**
 * Custom channel with embedded metrics
 */
export interface CustomChannel {
  custom_channel: string;
  custom_subchannel?: string;
  metrics?: CustomChannelMetrics;
}

/**
 * Custom channel with flattened metrics for CSV export
 */
export interface CustomChannelWithFlattenedMetrics {
  custom_channel: string;
  custom_subchannel?: string;
  [key: string]: string | number | undefined;
}

/**
 * Response type for custom channels list API
 */
export interface CustomChannelsResponse {
  results: CustomChannel[];
  total: number;
}

/**
 * Response type for field definitions
 */
export interface CustomChannelFieldDefinitionsResponse {
  field_definitions: CustomChannelFieldDefinition[];
}

/**
 * Parameters for custom channels query
 */
export interface CustomChannelsQueryParams extends QueryParams {
  metrics?: Array<keyof CustomChannelMetrics>;
  extra_fields?: string; // comma-separated, e.g., "custom_subchannel"
  metric_filters?: {
    account_filters?: FilterGroup;
    opportunity_filters?: FilterGroup;
    touchpoint_filters?: FilterGroup;
    campaign_filters?: FilterGroup;
  };
  rule_set_id?: string; // Selected ruleset id for channel classification
}

/**
 * Parameters for custom channel metrics query
 */
export interface CustomChannelMetricsParams {
  channel: string;
  subchannel?: string;
  metrics?: Array<keyof CustomChannelMetrics>;
  filters?: {
    account_filters?: FilterGroup;
    opportunity_filters?: FilterGroup;
    touchpoint_filters?: FilterGroup;
    campaign_filters?: FilterGroup;
  };
  rule_set_id?: string; // Selected ruleset id for channel classification
}

/**
 * Drilldown data types (reusing channel patterns)
 */
export interface AccountTouchedDrilldown {
  account_id: string;
  name: string;
  source_record_id: string;
  total_engagements: number;
  people_engaged: number;
  unique_people: number;
  first_engagement_date?: string;
  last_engagement_date?: string;
}

export interface TouchpointDrilldown {
  touchpoint_id: string;
  engagement_date: string;
  first_name?: string;
  last_name?: string;
  email_address?: string;
  account_name: string;
}

export interface PersonTouchedDrilldown {
  person_id: string;
  first_name?: string;
  last_name?: string;
  email_address?: string;
  job_title: string;
  account_name: string;
  total_engagements: number;
  first_engagement_date?: string;
  last_engagement_date?: string;
}

export type DrilldownData =
  | AccountTouchedDrilldown[]
  | TouchpointDrilldown[]
  | PersonTouchedDrilldown[];

// Map of which metrics are provided by which query groups
// Same as channel metrics but without sourced_opportunities (not supported for custom channels)
const METRIC_GROUPS = {
  basic_engagement: new Set([
    "accounts_engaged",
    "total_engagements",
    "people_engaged",
    "engagements_per_account",
    "engagements_per_person",
  ]),
  deal_metrics: new Set([
    "opportunities_influenced",
    "opportunities_influenced_amount",
    "opportunities_influenced_won",
    "opportunities_influenced_won_amount",
    "opportunities_influenced_in_cycle",
    "opportunities_influenced_in_cycle_amount",
    "opportunities_created_post_engagement",
    "opportunities_created_post_engagement_amount",
    "deal_contacts_engaged",
    "engagements_per_deal_contact",
  ]),
  progressed_opportunities: new Set([
    "progressed_opportunities",
    "progressed_opportunities_amount",
  ]),
  account_activation: new Set([
    "accounts_reactivated",
    "accounts_activated",
    "accounts_without_opps",
  ]),
  opportunities_activated: new Set([
    "opportunities_activated",
    "opportunities_activated_amount",
    "opportunities_activated_won",
    "opportunities_activated_won_amount",
  ]),
} as const;

// Map each metric to its group
const METRIC_TO_GROUP = Object.entries(METRIC_GROUPS).reduce(
  (acc, [group, metrics]) => {
    (metrics as Set<string>).forEach((metric) => {
      acc[metric] = group;
    });
    return acc;
  },
  {} as Record<string, string>,
);

interface MetricLoadingState {
  isLoading: boolean;
  isError: boolean;
  error: unknown;
}

interface UseCustomChannelMetricsResult {
  data: CustomChannelMetrics;
  isLoading: boolean;
  loadingStates: Record<keyof CustomChannelMetrics, MetricLoadingState>;
}

/**
 * Hook to fetch field definitions for custom channels
 */
export function useCustomChannelFieldDefinitions(rule_set_id?: string) {
  const searchParams = new URLSearchParams();
  if (rule_set_id) {
    searchParams.set("rule_set_id", rule_set_id);
  }
  const queryString = searchParams.toString();
  const endpoint = queryString
    ? `datahub/custom-channels/field-definitions/?${queryString}`
    : "datahub/custom-channels/field-definitions/";

  return useQuery<CustomChannelFieldDefinition[], Error>({
    queryKey: ["custom-channel-field-definitions", rule_set_id],
    queryFn: async () => {
      const response =
        await api.get<CustomChannelFieldDefinitionsResponse>(endpoint);
      return response.field_definitions || [];
    },
    staleTime: 10 * 60 * 1000, // 10 minutes - field definitions rarely change
  });
}

/**
 * Hook to fetch custom channels list with optional metrics
 */
export function useCustomChannels(params: CustomChannelsQueryParams = {}) {
  const searchParams = processQueryParams(params);

  const queryString = searchParams.toString();
  const endpoint = queryString
    ? `datahub/custom-channels/?${queryString}`
    : "datahub/custom-channels/";

  return api.useQuery<CustomChannelsResponse>(endpoint, {
    enabled: true,
  });
}

/**
 * Hook to fetch custom channels with embedded metrics (alias for useCustomChannels)
 */
export function useCustomChannelsWithMetrics(
  params: CustomChannelsQueryParams = {},
) {
  return useCustomChannels(params);
}

/**
 * Hook to fetch metrics for a specific custom channel
 *
 * We split the metrics into groups based on which queries they need.
 * This allows us to fetch the metrics in parallel, and to update the loading states for each group independently.
 */
export function useCustomChannelMetrics({
  channel,
  subchannel,
  metrics = [],
  filters = {},
  rule_set_id,
}: CustomChannelMetricsParams): UseCustomChannelMetricsResult {
  // Group metrics by their query group
  const metricsByGroup = metrics.reduce(
    (acc, metric) => {
      const group = METRIC_TO_GROUP[metric];
      if (group) {
        if (!acc[group]) {
          acc[group] = [];
        }
        acc[group].push(metric);
      }
      return acc;
    },
    {} as Record<string, Array<keyof CustomChannelMetrics>>,
  );

  // Encode channel name for URL
  const encodedChannel = encodeURIComponent(channel);

  // Create a query for each group that has metrics requested
  const queries = Object.entries(metricsByGroup).map(
    ([group, groupMetrics]) => {
      const searchParams = new URLSearchParams();
      groupMetrics.forEach((metric) => searchParams.append("metrics", metric));

      // Add subchannel if provided
      if (subchannel) {
        searchParams.append("subchannel", subchannel);
      }

      // Serialize filters object to JSON and add as a single parameter
      if (Object.keys(filters).length > 0) {
        searchParams.append("filters", JSON.stringify(filters));
      }

      // Add rule_set_id if provided
      if (rule_set_id) {
        searchParams.append("rule_set_id", rule_set_id);
      }

      return {
        group,
        metrics: groupMetrics,
        query: api.useQuery<CustomChannelMetrics>(
          `datahub/custom-channels/${encodedChannel}/metrics/?${searchParams}`,
          {
            enabled: !!channel,
          },
        ),
      };
    },
  );

  // Initialize loading states for all requested metrics
  const loadingStates = metrics.reduce(
    (acc, metric) => {
      acc[metric] = {
        isLoading: true,
        isError: false,
        error: null,
      };
      return acc;
    },
    {} as Record<keyof CustomChannelMetrics, MetricLoadingState>,
  );

  // Update loading states and combine data from all queries
  const data = queries.reduce((acc, { metrics: groupMetrics, query }) => {
    // Update loading states for this group's metrics
    groupMetrics.forEach((metric) => {
      loadingStates[metric] = {
        isLoading: query.isLoading,
        isError: !!query.error,
        error: query.error,
      };
    });

    // Add this group's data if available
    if (query.data) {
      return { ...acc, ...query.data };
    }
    return acc;
  }, {} as CustomChannelMetrics);

  // Overall loading state is true only if all metrics are still loading
  const isLoading = Object.values(loadingStates).every(
    (state) => state.isLoading,
  );

  return {
    data,
    isLoading,
    loadingStates,
  };
}

/**
 * Hook to fetch drilldown data for a specific custom channel metric
 */
export function useCustomChannelMetricDrilldown({
  channel,
  metric,
  subchannel,
  filters = {},
  enabled = true,
  rule_set_id,
}: {
  channel: string;
  metric: keyof CustomChannelMetrics;
  subchannel?: string;
  filters?: {
    account_filters?: FilterGroup;
    opportunity_filters?: FilterGroup;
    touchpoint_filters?: FilterGroup;
    campaign_filters?: FilterGroup;
  };
  enabled?: boolean;
  rule_set_id?: string;
}) {
  const searchParams = new URLSearchParams();

  // Add subchannel if provided
  if (subchannel) {
    searchParams.append("subchannel", subchannel);
  }

  // Serialize filters object to JSON and add as a single parameter
  if (Object.keys(filters).length > 0) {
    searchParams.append("filters", JSON.stringify(filters));
  }

  // Add rule_set_id if provided
  if (rule_set_id) {
    searchParams.append("rule_set_id", rule_set_id);
  }

  const queryString = searchParams.toString()
    ? `?${searchParams.toString()}`
    : "";

  // Encode channel name for URL
  const encodedChannel = encodeURIComponent(channel);

  return api.useQuery<DrilldownData>(
    `datahub/custom-channels/${encodedChannel}/metrics/${metric}/drilldown/${queryString}`,
    {
      enabled: enabled && !!channel,
    },
  );
}

/**
 * Fetch all custom channels matching the given filters for CSV export
 */
export function useCustomChannelsExport() {
  const mutation = useMutation<
    CustomChannelWithFlattenedMetrics[],
    Error,
    CustomChannelsQueryParams | undefined,
    unknown
  >({
    mutationFn: async (params = {}) => {
      // Process parameters into a URLSearchParams object
      const searchParams = processQueryParams({
        ...params,
        limit: 1000, // Set a high limit to get all custom channels
      });

      // Fetch all custom channels with metrics in a single call
      const response = await api.get<CustomChannelsResponse>(
        `datahub/custom-channels/?${searchParams}`,
      );

      // Transform to flatten metrics for CSV export
      return response.results.map((channel) => {
        const { metrics, ...baseObject } = channel;

        // Create a new object with all base properties
        const channelWithMetrics: CustomChannelWithFlattenedMetrics = {
          ...baseObject,
        };

        // Add each metric as a top-level property if metrics exist
        if (metrics) {
          Object.entries(metrics).forEach(([key, value]) => {
            // Only add primitive values to avoid nested objects in CSV
            if (
              typeof value === "string" ||
              typeof value === "number" ||
              typeof value === "boolean" ||
              value === null ||
              value === undefined
            ) {
              channelWithMetrics[`metric_${key}`] = value;
            }
          });
        }

        return channelWithMetrics;
      });
    },
  });

  // Return the mutation object and a convenience trigger function
  return {
    ...mutation,
    exportCustomChannelsAsync: (params?: CustomChannelsQueryParams) =>
      mutation.mutateAsync(params),
  };
}
